import { type FC, useState } from "react";
import {
  Button,
  Card,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import type {
  RestreamDestination,
  Stream,
  StreamContent,
} from "@/partner/entities/streams";
import {
  useDeleteRestreamDestinationMutation,
  useUpdateRestreamDestinationMutation,
} from "@/partner/entities/streams";
import { StreamDialogHeader } from "./StreamDialogHeader";
import { CreateRestreamDestinationDialog } from "./CreateRestreamDestinationDialog";
import { EditRestreamDestinationDialog } from "./EditRestreamDestinationDialog";

// Референсный shared/utils/truncate.js обрезает длинные url/key до 20
// символов, с trim() и тремя точками (не многоточием).
const truncate = (value: string, max = 20) =>
  value.length > max ? `${value.slice(0, max).trim()}...` : value;

interface StreamRestreamsTabProps {
  content: StreamContent;
  stream: Stream;
  onClose: () => void;
}

export const StreamRestreamsTab: FC<StreamRestreamsTabProps> = ({
  content,
  stream,
  onClose,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  // Референсный DialogHeaderButton.js: адаптивный размер кнопки хедера на
  // брейкпоинте md (900px) — наш useIsMobile по умолчанию режет на 768px.
  const isCompactHeader = useMediaQuery(theme.breakpoints.down("md"));
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<RestreamDestination | null>(null);

  const destinations = stream.restreamDestinations;
  const limit = stream.restreamDestinationsLimit;
  const atLimit = destinations.length >= limit;

  const [updateDestination] = useUpdateRestreamDestinationMutation({
    refetchQueries: ["Stream"],
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
  });
  const [deleteDestination] = useDeleteRestreamDestinationMutation({
    refetchQueries: ["Stream"],
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
  });

  const handleToggle = (id: string, enabled: boolean) => {
    void updateDestination({
      variables: { contentId: content.id, id, input: { enabled } },
    });
  };

  const handleDelete = (id: string) => {
    if (!window.confirm(t("streams.form.restream.confirmDelete"))) return;
    void deleteDestination({ variables: { contentId: content.id, id } });
  };

  return (
    <>
      <StreamDialogHeader
        title={t("streams.form.dialogTitle")}
        onClose={onClose}
      >
        <Tooltip
          title={
            atLimit
              ? t("streams.form.restream.limitReached", { limit })
              : t("streams.form.restream.available", {
                  count: destinations.length,
                  limit,
                })
          }
        >
          <span>
            <Button
              variant="contained"
              disabled={atLimit}
              onClick={() => setCreateOpen(true)}
              size={isCompactHeader ? "small" : "medium"}
              disableElevation
              sx={{ fontSize: isCompactHeader ? "0.7rem" : undefined }}
            >
              {t("streams.form.restream.addButton")}
            </Button>
          </span>
        </Tooltip>
      </StreamDialogHeader>

      <Card elevation={0} sx={{ overflowX: "auto", scrollbarWidth: "none" }}>
        {destinations.length === 0 ? (
          <Typography
            color="text.secondary"
            align="center"
            sx={{ py: 6, px: 2 }}
          >
            {t("streams.form.restream.empty")}
          </Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("streams.form.restream.titleColumn")}</TableCell>
                <TableCell sx={{ width: 150 }}>
                  {t("streams.form.restream.enabledColumn")}
                </TableCell>
                <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                  {t("streams.form.restream.urlColumn")}
                </TableCell>
                <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                  {t("streams.form.restream.keyColumn")}
                </TableCell>
                <TableCell align="right">
                  {t("streams.form.restream.manageColumn")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {destinations.map((destination) => (
                <TableRow
                  key={destination.id}
                  hover
                  // Референсный Restreams.js: выключенное назначение
                  // затухает целиком, строка кликабельна на редактирование.
                  sx={{
                    cursor: "pointer",
                    opacity: destination.enabled ? 1 : 0.3,
                  }}
                  onClick={() => setEditing(destination)}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {destination.title}
                  </TableCell>
                  <TableCell onClick={(event) => event.stopPropagation()}>
                    <Switch
                      checked={destination.enabled}
                      onChange={(event) =>
                        handleToggle(destination.id, event.target.checked)
                      }
                    />
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    {truncate(destination.url)}
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    {truncate(destination.streamKey)}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t("common.actions.delete")}>
                      <IconButton
                        size="large"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDelete(destination.id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <CreateRestreamDestinationDialog
        contentId={content.id}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <EditRestreamDestinationDialog
        contentId={content.id}
        destination={editing}
        onClose={() => setEditing(null)}
      />
    </>
  );
};
